import { Document } from '@react-pdf/renderer';

import { CostApproachPage } from './pages/CostApproachPage.tsx';
import { CoverPage } from './pages/CoverPage.tsx';
import { DateOfSaleAdjustmentsPage } from './pages/DateOfSaleAdjustmentsPage.tsx';
import { MarketConditionGraphsPage } from './pages/MarketConditionGraphsPage.tsx';
import { MarketConditionPage } from './pages/MarketConditionPage.tsx';
import { MethodologyPage } from './pages/MethodologyPage.tsx';
import { PriorSalesPage } from './pages/PriorSalesPage.tsx';
import { PropertyOrderDetailsPage } from './pages/PropertyOrderDetailsPage.tsx';
import { ReconciliationPage } from './pages/ReconciliationPage.tsx';
import { SalesComparablePhotosPage } from './pages/SalesComparablePhotosPage.tsx';
import { SalesComparisonMapPage } from './pages/SalesComparisonMapPage.tsx';
import { SalesComparisonPage } from './pages/SalesComparisonPage.tsx';
import { SummaryPage } from './pages/SummaryPage.tsx';
import { UadDefinitionsPage } from './pages/UadDefinitionsPage.tsx';
import { CertificationSignaturePage } from './pages/certification/CertificationSignaturePage.tsx';
import { CertificationStatementPage } from './pages/certification/CertificationStatementPage.tsx';
import { Tkv2PDF, Tkv2PDFChartImages, Tkv2SignatureImages } from './types.ts';

export const Tkv2PDFModule = ({
  subjectAddress,
  opinionOfMarketValue,
  totalCostToCure,
  subjectPropertyImage,
  clientName,
  clientAddress,
  appraiserFee,
  amcFee,
  effectiveDate,
  propertyType,
  shortPropertyType,
  locationType,
  inspectorType,
  orderNumber,
  intendedUsers,
  intendedUse,
  borrowerName,
  inspectionDate,
  zoning,
  zoningCompliance,
  parcelNumber,
  propertyInterest,
  hoa,
  hoaFees,
  pud,
  legalDescription,
  propertyMapImage,
  appraisalType,
  mapBoxCoordinates,
  mapStatistics,
  subjectSalesComparisonData,
  compsForMaps,
  subjectCoordinates,
  comparablesSalesComparisonData,
  adjustmentSettings,
  commentaryV2,
  adjustmentsTableData,
  tkv2ChartImages,
  licenseExpiration,
  licenseNumber,
  licenseState,
  licenseType,
  signedAt,
  appraiserName,
  signatureImages,
  compSequence,
  priorSales,
  inspectionData,
  includeTotalCostToCure,
  totalRenovationBudget,
  opinionOfAfterRepairValue,
  estimatedROI,
  costApproachData,
  reconciliationPageData,
  isRevision,
  revisionType,
  alerts,
  marketAggregateData,
  countyName,
  countyFIPS,
  revisionData,
  previousRevisionsData,
  salesComparablePhotosData,
  hasArvOption,
  compTypes,
  subjectArvSalesComparisonData,
  salesComparisonApproach,
  includeOpinionOfAfterRepairValue,
  includeTotalRenovationBudget,
  hasNegativeROI,
  valueType,
  appraisalReportDisclaimer,
  includeCostApproach,
  orderFeatures,
  clientLogoUrl,
  isInternalValuation,
}: Tkv2PDF & { tkv2ChartImages: Tkv2PDFChartImages; signatureImages: Tkv2SignatureImages }) => (
  <Document>
    <CoverPage
      subjectAddress={subjectAddress}
      opinionOfMarketValue={opinionOfMarketValue}
      totalCostToCure={totalCostToCure}
      subjectPropertyImage={subjectPropertyImage ?? ''}
      intendedUsers={intendedUsers}
      effectiveDate={effectiveDate}
      shortPropertyType={shortPropertyType}
      inspectorType={inspectorType}
      appraisalType={appraisalType}
      includeTotalCostToCure={includeTotalCostToCure}
      hasArvOption={hasArvOption}
      totalRenovationBudget={totalRenovationBudget}
      opinionOfAfterRepairValue={opinionOfAfterRepairValue}
      estimatedROI={estimatedROI}
      clientName={clientName}
      includeOpinionOfAfterRepairValue={includeOpinionOfAfterRepairValue}
      includeTotalRenovationBudget={includeTotalRenovationBudget}
      inspectionDate={inspectionDate}
      valueType={valueType}
      inspectionData={inspectionData}
      appraisalReportDisclaimer={appraisalReportDisclaimer}
      isInternalValuation={isInternalValuation}
      clientLogoUrl={clientLogoUrl}
    />
    {isInternalValuation ? (
      <>
        <SalesComparisonPage
          subjectAddress={subjectAddress}
          subjectSalesComparisonData={subjectSalesComparisonData}
          subjectArvSalesComparisonData={subjectArvSalesComparisonData}
          comparables={Object.values(comparablesSalesComparisonData)}
          hasArvOption={hasArvOption}
          includeTotalCostToCure={includeTotalCostToCure}
          includeCostApproach={includeCostApproach}
          salesComparisonApproach={salesComparisonApproach}
          orderFeatures={orderFeatures}
          opinionOfMarketValue={opinionOfMarketValue}
          opinionOfAfterRepairValue={opinionOfAfterRepairValue}
        />
        <PriorSalesPage
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          compSequence={compSequence}
          priorSales={priorSales}
          compTypes={compTypes}
        />
        {subjectSalesComparisonData && comparablesSalesComparisonData && (
          <SalesComparisonMapPage
            subjectAddress={subjectAddress}
            subjectLatitude={subjectCoordinates?.latitude}
            subjectLongitude={subjectCoordinates?.longitude}
            comps={compsForMaps}
            mapBoxCoordinates={mapBoxCoordinates}
            statistics={mapStatistics}
            compSequence={compSequence}
            compTypes={compTypes}
            hasArvOption={hasArvOption}
          />
        )}
        <SummaryPage
          subjectAddress={subjectAddress}
          includeTotalCostToCure={includeTotalCostToCure}
          includeTotalRenovationBudget={includeTotalRenovationBudget}
          includeOpinionOfAfterRepairValue={includeOpinionOfAfterRepairValue}
          totalCostToCure={totalCostToCure}
          totalRenovationBudget={totalRenovationBudget}
          opinionOfMarketValue={opinionOfMarketValue}
          opinionOfAfterRepairValue={opinionOfAfterRepairValue}
          estimatedROI={estimatedROI}
          hasNegativeROI={hasNegativeROI}
          effectiveDate={effectiveDate}
          tkv2ChartImages={tkv2ChartImages}
          aiv={reconciliationPageData.aiv}
          arv={reconciliationPageData.arv}
          includeCostApproach={includeCostApproach}
        />
        <DateOfSaleAdjustmentsPage
          subjectAddress={subjectAddress}
          adjustmentSettings={adjustmentSettings}
          adjustmentsTableData={adjustmentsTableData}
          tkv2ChartImages={tkv2ChartImages}
          compSequence={compSequence}
          compTypes={compTypes}
        />
        {includeCostApproach && (
          <CostApproachPage
            subjectAddress={subjectAddress}
            costApproachData={costApproachData}
            siteSizeVsSiteValueChartImage={tkv2ChartImages.siteSizeVsSiteValueChartImage}
            salesComparisonApproach={salesComparisonApproach}
          />
        )}
        <MarketConditionPage
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          marketAggregateData={marketAggregateData}
          countyName={countyName}
          countyFIPS={countyFIPS}
        />
        <MarketConditionGraphsPage
          subjectAddress={subjectAddress}
          tkv2ChartImages={tkv2ChartImages}
        />
      </>
    ) : (
      <>
        <SummaryPage
          subjectAddress={subjectAddress}
          includeTotalCostToCure={includeTotalCostToCure}
          includeTotalRenovationBudget={includeTotalRenovationBudget}
          includeOpinionOfAfterRepairValue={includeOpinionOfAfterRepairValue}
          totalCostToCure={totalCostToCure}
          totalRenovationBudget={totalRenovationBudget}
          opinionOfMarketValue={opinionOfMarketValue}
          opinionOfAfterRepairValue={opinionOfAfterRepairValue}
          estimatedROI={estimatedROI}
          hasNegativeROI={hasNegativeROI}
          effectiveDate={effectiveDate}
          tkv2ChartImages={tkv2ChartImages}
          aiv={reconciliationPageData.aiv}
          arv={reconciliationPageData.arv}
          includeCostApproach={includeCostApproach}
        />

        <PropertyOrderDetailsPage
          subjectAddress={subjectAddress}
          shortPropertyType={shortPropertyType}
          locationType={locationType}
          appraisalType={appraisalType}
          effectiveDate={effectiveDate}
          inspectionDate={inspectionDate}
          clientName={clientName}
          clientAddress={clientAddress}
          borrowerName={borrowerName}
          zoning={zoning}
          zoningCompliance={zoningCompliance}
          parcelNumber={parcelNumber}
          propertyInterest={propertyInterest}
          hoa={hoa}
          hoaFees={hoaFees}
          pud={pud}
          legalDescription={legalDescription}
          orderNumber={orderNumber}
          intendedUsers={intendedUsers}
          intendedUse={intendedUse}
          propertyMapImage={propertyMapImage}
          hasArvOption={hasArvOption}
          subjectSalesComparisonData={subjectSalesComparisonData}
          subjectArvSalesComparisonData={subjectArvSalesComparisonData}
        />
        <SalesComparisonPage
          subjectAddress={subjectAddress}
          subjectSalesComparisonData={subjectSalesComparisonData}
          subjectArvSalesComparisonData={subjectArvSalesComparisonData}
          comparables={Object.values(comparablesSalesComparisonData)}
          hasArvOption={hasArvOption}
          includeTotalCostToCure={includeTotalCostToCure}
          includeCostApproach={includeCostApproach}
          salesComparisonApproach={salesComparisonApproach}
          orderFeatures={orderFeatures}
          opinionOfMarketValue={opinionOfMarketValue}
          opinionOfAfterRepairValue={opinionOfAfterRepairValue}
        />
        {subjectSalesComparisonData && comparablesSalesComparisonData && (
          <SalesComparisonMapPage
            subjectAddress={subjectAddress}
            subjectLatitude={subjectCoordinates?.latitude}
            subjectLongitude={subjectCoordinates?.longitude}
            comps={compsForMaps}
            mapBoxCoordinates={mapBoxCoordinates}
            statistics={mapStatistics}
            compSequence={compSequence}
            compTypes={compTypes}
            hasArvOption={hasArvOption}
          />
        )}
        <DateOfSaleAdjustmentsPage
          subjectAddress={subjectAddress}
          adjustmentSettings={adjustmentSettings}
          adjustmentsTableData={adjustmentsTableData}
          tkv2ChartImages={tkv2ChartImages}
          compSequence={compSequence}
          compTypes={compTypes}
        />
        {includeCostApproach && (
          <CostApproachPage
            subjectAddress={subjectAddress}
            costApproachData={costApproachData}
            siteSizeVsSiteValueChartImage={tkv2ChartImages.siteSizeVsSiteValueChartImage}
            salesComparisonApproach={salesComparisonApproach}
          />
        )}
        <MarketConditionPage
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          marketAggregateData={marketAggregateData}
          countyName={countyName}
          countyFIPS={countyFIPS}
        />
        <MarketConditionGraphsPage
          subjectAddress={subjectAddress}
          tkv2ChartImages={tkv2ChartImages}
        />
        <PriorSalesPage
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          compSequence={compSequence}
          priorSales={priorSales}
          compTypes={compTypes}
        />
        <ReconciliationPage
          subjectAddress={subjectAddress}
          isRevision={isRevision}
          revisionType={revisionType}
          commentaryV2={commentaryV2}
          alerts={alerts}
          revisionData={revisionData}
          previousRevisionsData={previousRevisionsData}
          includeTotalCostToCure={includeTotalCostToCure}
          includeOpinionOfAfterRepairValue={includeOpinionOfAfterRepairValue}
          includeTotalRenovationBudget={includeTotalRenovationBudget}
          opinionOfMarketValue={opinionOfMarketValue}
          totalCostToCure={totalCostToCure}
          opinionOfAfterRepairValue={opinionOfAfterRepairValue}
          totalRenovationBudget={totalRenovationBudget}
          estimatedROI={estimatedROI}
          effectiveDate={effectiveDate}
          hasNegativeROI={hasNegativeROI}
          hasArvOption={hasArvOption}
          {...reconciliationPageData}
        />
        <CertificationStatementPage
          subjectAddress={subjectAddress}
          hasArvOption={hasArvOption}
          inspectionData={inspectionData}
        />
        <CertificationSignaturePage
          subjectAddress={subjectAddress}
          commentaryV2={commentaryV2}
          effectiveDate={effectiveDate}
          inspectionDate={inspectionDate}
          licenseNumber={licenseNumber}
          licenseState={licenseState}
          licenseExpiration={licenseExpiration}
          licenseType={licenseType}
          signedAt={signedAt}
          appraiserName={appraiserName}
          signatureImages={signatureImages}
          inspectionData={inspectionData}
        />
        <MethodologyPage
          subjectAddress={subjectAddress}
          propertyType={propertyType}
          appraiserFee={appraiserFee}
          amcFee={amcFee}
          hasArvOption={hasArvOption}
          inspectionData={inspectionData}
        />
        <UadDefinitionsPage subjectAddress={subjectAddress} />
        <SalesComparablePhotosPage
          subjectAddress={subjectAddress}
          salesComparablePhotosData={salesComparablePhotosData}
          compSequence={compSequence}
        />
      </>
    )}
  </Document>
);
